import Box from "@mui/material/Box";
import { useContext, useState } from "react";
import Modal from "@mui/material/Modal";
import { useForm } from "react-hook-form";
import LogicContext from "../../Context/LogicContext";
import { productService } from "../../services/product.service";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "8px",
};

const ProductAddModal = (props) => {
  const {
    register,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [open, setOpen] = useState(false);
  const logicContext = useContext(LogicContext);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const onSubmit = async () => {
    const newData = getValues();
    const option = 1;

    try {
      const res = await productService.saveNewProduct({ ...newData, option });
      if (res?.data) {
        const { message_text } = res.data[0];
        logicContext.showToast(message_text, "success");
        handleClose();
        reset();
        props.reloadProductList();
      }
    } catch (err) {
      logicContext.showToast("Failed to save product", "error");
    }
  };

  return (
    <>
      <button
        onClick={handleOpen}
        className="bg-blue-600 text-white hover:bg-blue-700 flex flex-row items-center text-sm font-medium px-3 py-2 rounded-md transition-colors"
      >
        Add Product
      </button>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={modalStyle}
          className="h-fit max-h-[calc(100vh-40px)] w-full max-w-[500px] overflow-auto "
        >
          <div className="flex items-center justify-end pt-1">
            <button
              onClick={handleClose}
              type="button"
              className="p-1.5 rounded-full hover:bg-gray-200 active:bg-gray-100"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="size-5 text-gray-700"
              >
                <path
                  fillRule="evenodd"
                  d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="px-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold">Add New Product</h2>
            </div>

            <div className="space-y-4">
              {/* Product Name */}
              <div>
                <label className="text-sm font-medium">Product Name</label>
                <input
                  {...register("Product_Name", {
                    required: "Product name is required",
                  })}
                  type="text"
                  placeholder="Enter product name"
                  className="mt-1 !h-10 border border-gray-200 rounded placeholder:text-gray-400 w-full focus:!border-blue-500 focus:!ring-1 focus:!ring-blue-500"
                />
                {errors?.Product_Name && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.Product_Name.message}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium">
                  Description <i>(Optional)</i>
                </label>
                <input
                  {...register("Product_Description")}
                  type="text"
                  placeholder="Write description"
                  className="mt-1 !h-10 border border-gray-200 rounded placeholder:text-gray-400 w-full focus:!border-blue-500 focus:!ring-1 focus:!ring-blue-500"
                />
              </div>

              {/* Notification Email */}
              <div>
                <label className="text-sm font-medium">
                  Notification Mail Address
                </label>
                <p className="text-xs text-gray-500 mb-1">
                  Enter comma-separated emails to receive alerts.
                </p>
                <input
                  {...register("Mail_Address")}
                  type="text"
                  placeholder="example@mail.com"
                  className="mt-1 !h-10 border border-gray-200 rounded placeholder:text-gray-400 w-full focus:!border-blue-500 focus:!ring-1 focus:!ring-blue-500"
                />
              </div>

              {/* Redirect URL */}
              <div>
                <label className="text-sm font-medium">Redirect URL</label>
                <p className="text-xs text-gray-500 mb-1">
                  Clients will be redirected to this URL after payment.
                </p>
                <input
                  {...register("Notification_Url")}
                  type="text"
                  placeholder="https://yourdomain.com/thank-you"
                  className="mt-1 !h-10 border border-gray-200 rounded placeholder:text-gray-400 w-full focus:!border-blue-500 focus:!ring-1 focus:!ring-blue-500"
                />
                <p className="text-xs italic mt-1 text-gray-400">
                  If left empty, users are redirected to the default thank-you
                  page.
                </p>
              </div>

              {/* Auto Generate Checkbox */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded-sm checked:ring-0 focus:ring-0 cursor-pointer"
                />
                <span className="text-sm">Auto-generate Product Numbers</span>
              </div>

              {/* Prefix and Next Number */}
              <div className="flex gap-4">
                <div className="w-1/2">
                  <label className="text-sm font-medium">
                    Prefix (Alphanumeric)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. PROD"
                    className="mt-1 border border-gray-200 rounded placeholder:text-gray-400 w-full focus:!border-blue-500 focus:!ring-1 focus:!ring-blue-500"
                  />
                </div>
                <div className="w-1/2">
                  <label className="text-sm font-medium">
                    Next Number (Numeric)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 1001"
                    className="mt-1 border border-gray-200 rounded placeholder:text-gray-400 w-full focus:!border-blue-500 focus:!ring-1 focus:!ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end mt-6 mb-10 gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm rounded-md"
              >
                Save
              </button>
            </div>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default ProductAddModal;
